import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Toast from './Toast';
import useToast from '../hooks/useToast';
import {
  getManagerImputationsTable,
  formatApiError,
  updateImputation,
  getCurrentUserProfile,
  getTenantSubdomain,
} from '../services/api';
import { getDateLocale } from '../i18n';
import {
  getImputationEtatFilterOptions,
  getImputationEtatOptions,
  RS_ETAT_CODES,
  getEtatBadgeClasses,
} from '../utils/imputationEtats';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const DATE_RANGE_PRESETS = [
  { value: 'last_24h', labelKey: 'imputations.last24h' },
  { value: 'last_7_days', labelKey: 'imputations.last7Days' },
  { value: 'this_month', labelKey: 'imputations.thisMonth' },
  { value: 'last_month', labelKey: 'imputations.lastMonth' },
  { value: 'this_quarter', labelKey: 'imputations.thisQuarter' },
  { value: 'custom', labelKey: 'imputations.customRange' },
];

function getDateRangeForPreset(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const y = today.getFullYear();
  const m = today.getMonth();
  const pad = (n) => String(n).padStart(2, '0');
  const toStr = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

  switch (preset) {
    case 'last_24h':
      return { date_from: toStr(today), date_to: toStr(today) };
    case 'last_7_days': {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return { date_from: toStr(from), date_to: toStr(today) };
    }
    case 'this_month': {
      const from = new Date(y, m, 1);
      return { date_from: toStr(from), date_to: toStr(today) };
    }
    case 'last_month': {
      const from = new Date(y, m - 1, 1);
      const to = new Date(y, m, 0);
      return { date_from: toStr(from), date_to: toStr(to) };
    }
    case 'this_quarter': {
      const firstMonth = Math.floor(m / 3) * 3;
      const from = new Date(y, firstMonth, 1);
      return { date_from: toStr(from), date_to: toStr(today) };
    }
    default:
      return null;
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString(getDateLocale());
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString(getDateLocale(), { dateStyle: 'short', timeStyle: 'short' });
}

/** Format decimal hours as HH:MM (e.g. 1.5 → "01:30") */
function formatHoursToHHMM(hours) {
  if (hours == null || hours === '') return '—';
  const h = Number(hours);
  if (Number.isNaN(h)) return '—';
  const whole = Math.floor(h);
  const mins = Math.round((h - whole) * 60);
  const mm = mins % 60;
  const hh = whole + Math.floor(mins / 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function displayUser(user) {
  if (!user) return '—';
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return name || user.email || '—';
}

const ImputationsTablePage = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [showComments, setShowComments] = useState(true);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Admin-only actions (status updates)
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const isAdmin = String(currentUser?.role || '').toUpperCase() === 'ADMIN';

  const { toast, showSuccess, showWarning, showError, hideToast } = useToast();
  const [statusDraftById, setStatusDraftById] = useState({});
  const [updatingStatusById, setUpdatingStatusById] = useState({});

  const [filterOptions, setFilterOptions] = useState({
    centres: [],
    teams: [],
    employees: [],
    domains: [],
    operations: [],
    production_lines: [],
    project_types: [],
    programs: [],
  });
  const defaultDateRange = getDateRangeForPreset('last_7_days');
  const [dateRangePreset, setDateRangePreset] = useState('last_7_days');
  const [filters, setFilters] = useState({
    date_from: defaultDateRange?.date_from ?? '',
    date_to: defaultDateRange?.date_to ?? '',
    centre: '',
    team: '',
    employee: '',
    domain: '',
    operation: '',
    etat_final: '',
    production_line_lp: '',
    project_type: '',
    program: '',
  });

  const isInitialPreset = useRef(true);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setLoadingUser(true);
        const userData = await getCurrentUserProfile();
        setCurrentUser(userData);
      } catch (e) {
        // Safe fallback: non-admin
        setCurrentUser({ role: 'EMPLOYE' });
      } finally {
        setLoadingUser(false);
      }
    };

    fetchCurrentUser();
  }, []);

  const isRsTenant = (getTenantSubdomain() || '').toLowerCase() === 'rs';
  const ALLOWED_ETAT_FINAL_VALUES = isRsTenant ? RS_ETAT_CODES : ['TERMINE', 'ECHEC'];
  const etatFilterOptions = React.useMemo(() => getImputationEtatFilterOptions(getTenantSubdomain()), []);
  const canEditImputation = (row) => {
    // Admins can update status regardless of creation time.
    if (isAdmin) return true;
    if (!row?.date_saisie) return false;
    const d = new Date(row.date_saisie);
    if (Number.isNaN(d.getTime())) return false;
    const hoursDiff = (Date.now() - d.getTime()) / (1000 * 60 * 60);
    return hoursDiff < 24;
  };

  const getEtatUpdateChoices = (row) => {
    const baseChoices = getImputationEtatOptions(getTenantSubdomain()).map((c) => ({ ...c }));

    const currentValue = row?.etat_final;
    if (currentValue && !baseChoices.some((c) => c.value === currentValue)) {
      baseChoices.push({
        value: currentValue,
        label: row?.etat_final_display || currentValue,
        disabled: true,
      });
    }

    return baseChoices;
  };

  const handleAdminUpdateStatus = async (row, newEtatFinal) => {
    if (!row || !row.id) return;
    if (!isAdmin) {
      showWarning(t('imputations.permissionDenied'), t('imputations.permissionDeniedMessage'));
      return;
    }
    if (!canEditImputation(row)) {
      showWarning(t('imputations.modificationRestricted'), t('imputations.modificationRestrictedMessage'));
      return;
    }
    if (!ALLOWED_ETAT_FINAL_VALUES.includes(newEtatFinal)) {
      showWarning(t('imputations.invalidStatus'), t('imputations.invalidStatusMessage'));
      return;
    }

    const isUpdating = !!updatingStatusById[row.id];
    if (isUpdating) return;

    const operationId = row?.operation?.id;
    if (!operationId) {
      showError(t('imputations.updateFailed'), t('imputations.missingOperationId'));
      return;
    }

    const payload = {
      date_operation: row.date_operation,
      gcr: row.gcr || 'N/A',
      operation_id: operationId,
      etat_final: newEtatFinal,
      commentaire: row.commentaire || '',
      modification_reason: t('imputations.adminStatusUpdateReason', { status: newEtatFinal }),
    };

    try {
      setUpdatingStatusById((prev) => ({ ...prev, [row.id]: true }));
      await updateImputation(row.id, payload);
      showSuccess(t('imputations.statusUpdated'), t('imputations.statusUpdatedMessage', { id: row.id, status: newEtatFinal }));
      setStatusDraftById((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      // Refresh current page with the active filters
      fetchData(page, pageSize, filters);
    } catch (e) {
      const formatted = formatApiError(e);
      showError(t('imputations.updateFailed'), formatted.generalError || t('imputations.updateFailedMessage'));
    } finally {
      setUpdatingStatusById((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  // When preset changes (not custom), update filters with computed date range
  useEffect(() => {
    if (isInitialPreset.current) {
      isInitialPreset.current = false;
      return;
    }
    if (dateRangePreset && dateRangePreset !== 'custom') {
      const range = getDateRangeForPreset(dateRangePreset);
      if (range) {
        setFilters((f) => ({ ...f, date_from: range.date_from, date_to: range.date_to }));
      }
    }
  }, [dateRangePreset]);

  // Employee combobox: search input + dropdown
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const employeeRef = useRef(null);

  // Operation combobox: search input + dropdown
  const [operationSearch, setOperationSearch] = useState('');
  const [showOperationDropdown, setShowOperationDropdown] = useState(false);
  const operationRef = useRef(null);

  const getFilteredEmployees = (term) => {
    const list = filterOptions.employees || [];
    if (!term.trim()) return list;
    const t = term.toLowerCase();
    return list.filter(
      (u) =>
        (u.first_name || '').toLowerCase().includes(t) ||
        (u.last_name || '').toLowerCase().includes(t) ||
        (u.email || '').toLowerCase().includes(t) ||
        (u.name || '').toLowerCase().includes(t)
    );
  };

  // MULTI-DOMAINE operations always appear in the list (not filtered by search)
  const getFilteredOperations = (term) => {
    const list = filterOptions.operations || [];
    if (!term.trim()) return list;
    const t = term.toLowerCase();
    const matching = list.filter(
      (op) =>
        (op.production_line_lp || '').toLowerCase().includes(t) || (op.activity || '').toLowerCase().includes(t)
    );
    const multiDomaine = list.filter(
      (op) => op.domain && String(op.domain).toUpperCase().includes('MULTI-DOMAINE')
    );
    const seen = new Set();
    return [...matching, ...multiDomaine].filter((op) => {
      if (seen.has(op.id)) return false;
      seen.add(op.id);
      return true;
    });
  };

  const selectedEmployee = (filterOptions.employees || []).find((u) => String(u.id) === String(filters.employee));
  const selectedEmployeeLabel = selectedEmployee
    ? [selectedEmployee.first_name, selectedEmployee.last_name].filter(Boolean).join(' ').trim() || selectedEmployee.email || ''
    : '';
  const selectedOperation = (filterOptions.operations || []).find((op) => String(op.id) === String(filters.operation));
  const selectedOperationLabel = selectedOperation ? `${selectedOperation.production_line_lp || ''} – ${selectedOperation.activity || ''}`.trim() : '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (employeeRef.current && !employeeRef.current.contains(e.target)) setShowEmployeeDropdown(false);
      if (operationRef.current && !operationRef.current.contains(e.target)) setShowOperationDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = useCallback(async (pageNum = 1, pageSizeVal = pageSize, applied = filters) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getManagerImputationsTable(applied, pageNum, pageSizeVal);
      setResults(res.results || []);
      setCount(res.count ?? 0);
      setPage(pageNum);
      setPageSize(pageSizeVal);
      setTotalPages(Math.ceil((res.count || 0) / pageSizeVal) || 1);
      if (res.filter_options) {
        setFilterOptions(res.filter_options);
      }
    } catch (err) {
      setError(formatApiError(err));
      setResults([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [pageSize, filters]);

  // Apply filters on change: fetch when filters change (reset to page 1)
  useEffect(() => {
    fetchData(1, pageSize, filters);
  }, [filters]);

  const handleResetFilters = () => {
    const presetRange = getDateRangeForPreset('last_7_days');
    setDateRangePreset('last_7_days');
    setFilters({
      date_from: presetRange?.date_from ?? '',
      date_to: presetRange?.date_to ?? '',
      centre: '',
      team: '',
      employee: '',
      domain: '',
      operation: '',
      etat_final: '',
      production_line_lp: '',
      project_type: '',
      program: '',
    });
    setEmployeeSearch('');
    setOperationSearch('');
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    const p = Math.max(1, Math.min(newPage, totalPages));
    setPage(p);
    fetchData(p, pageSize, filters);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value) || 20;
    setPageSize(newSize);
    setPage(1);
    fetchData(1, newSize, filters);
  };

  const startItem = count === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, count);
  const hasActiveFilters =
    dateRangePreset !== 'last_7_days' ||
    !!(
      filters.centre ||
      filters.team ||
      filters.employee ||
      filters.domain ||
      filters.operation ||
      filters.etat_final ||
      filters.production_line_lp ||
      filters.project_type ||
      filters.program
    );

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) pages.push(i);
      }
      if (page < totalPages - 2) pages.push('...');
      if (totalPages > 1) pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Toast Notification */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        duration={toast.duration}
      />

      {/* Page Heading - same as employee page */}
      <div className="flex flex-wrap justify-between items-end gap-4 px-1">
        <div className="flex min-w-72 flex-col gap-2">
          <h1 className="text-slate-900 text-3xl font-bold leading-tight tracking-[-0.02em]">
            {t('imputations.title')}
          </h1>
          <p className="text-slate-500 text-base font-normal leading-normal">
            {t('imputations.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowComments((v) => !v)}
          className={`flex items-center justify-center gap-2 rounded-lg h-10 px-4 text-sm font-semibold border shadow-sm transition-colors ${
            showComments
              ? 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20 dark:bg-primary/20 dark:border-primary/50 dark:hover:bg-primary/30'
              : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700'
          }`}
          title={showComments ? t('imputations.hideCommentsTitle') : t('imputations.showCommentsTitle')}
        >
          <span className="material-symbols-outlined text-[20px]">comment</span>
          <span>{showComments ? t('imputations.hideComments') : t('imputations.showComments')}</span>
        </button>
      </div>

      {/* Filters - applied on change */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 items-end">
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.dateRange')}</p>
            <div className="relative">
              <select
                value={dateRangePreset}
                onChange={(e) => setDateRangePreset(e.target.value)}
                className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
              >
                {DATE_RANGE_PRESETS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </select>
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
            </div>
          </div>
          {dateRangePreset === 'custom' && (
            <>
              <div className="flex flex-col gap-2">
                <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.dateFrom')}</p>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-4 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.dateTo')}</p>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-4 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
            </>
          )}
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.center')}</p>
            <div className="relative">
              <select
                value={filters.centre}
                onChange={(e) => setFilters((f) => ({ ...f, centre: e.target.value }))}
                className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
              >
                <option value="">{t('common:all')}</option>
                {filterOptions.centres?.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('common:team')}</p>
            <div className="relative">
              <select
                value={filters.team}
                onChange={(e) => setFilters((f) => ({ ...f, team: e.target.value }))}
                className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
              >
                <option value="">{t('common:all')}</option>
                {filterOptions.teams?.map((team) => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 relative" ref={employeeRef}>
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.employee')}</p>
            <div className="relative">
              <input
                type="text"
                value={showEmployeeDropdown ? employeeSearch : selectedEmployeeLabel}
                onChange={(e) => {
                  setEmployeeSearch(e.target.value);
                  setShowEmployeeDropdown(true);
                }}
                onFocus={() => {
                  setShowEmployeeDropdown(true);
                  setEmployeeSearch(selectedEmployeeLabel);
                }}
                placeholder={t('imputations.searchEmployee')}
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary placeholder-slate-400"
              />
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">search</span>
              {showEmployeeDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  <div
                    onClick={() => {
                      setFilters((f) => ({ ...f, employee: '' }));
                      setEmployeeSearch('');
                      setShowEmployeeDropdown(false);
                    }}
                    className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100 font-medium text-slate-700"
                  >
                    {t('imputations.allEmployees')}
                  </div>
                  {getFilteredEmployees(employeeSearch).map((u) => (
                    <div
                      key={u.id}
                      onClick={() => {
                        setFilters((f) => ({ ...f, employee: String(u.id) }));
                        setEmployeeSearch([u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || '');
                        setShowEmployeeDropdown(false);
                      }}
                      className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100 last:border-b-0"
                    >
                      <div className="font-medium text-slate-900">
                        {[u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || '—'}
                      </div>
                      {u.email && (
                        <div className="text-xs text-slate-500">{u.email}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.domain')}</p>
            <div className="relative">
              <select
                value={filters.domain}
                onChange={(e) => setFilters((f) => ({ ...f, domain: e.target.value }))}
                className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
              >
                <option value="">{t('common:all')}</option>
                {filterOptions.domains?.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
            </div>
          </div>
          {!isRsTenant && (
            <div className="flex flex-col gap-2">
              <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.productionLinePl')}</p>
              <div className="relative">
                <select
                  value={filters.production_line_lp}
                  onChange={(e) => setFilters((f) => ({ ...f, production_line_lp: e.target.value }))}
                  className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
                >
                  <option value="">{t('common:all')}</option>
                  {filterOptions.production_lines?.map((pl) => (
                    <option key={pl} value={pl}>{pl}</option>
                  ))}
                </select>
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
            </div>
          )}
          {!isRsTenant && (
            <div className="flex flex-col gap-2">
              <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.projectType')}</p>
              <div className="relative">
                <select
                  value={filters.project_type}
                  onChange={(e) => setFilters((f) => ({ ...f, project_type: e.target.value }))}
                  className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
                >
                  <option value="">{t('common:all')}</option>
                  {filterOptions.project_types?.map((pt) => (
                    <option key={pt} value={pt}>{pt}</option>
                  ))}
                </select>
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.program')}</p>
            <div className="relative">
              <select
                value={filters.program}
                onChange={(e) => setFilters((f) => ({ ...f, program: e.target.value }))}
                className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
              >
                <option value="">{t('common:all')}</option>
                {filterOptions.programs?.map((pg) => (
                  <option key={pg} value={pg}>{pg}</option>
                ))}
              </select>
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 relative" ref={operationRef}>
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('imputations.operation')}</p>
            <div className="relative">
              <input
                type="text"
                value={showOperationDropdown ? operationSearch : selectedOperationLabel}
                onChange={(e) => {
                  setOperationSearch(e.target.value);
                  setShowOperationDropdown(true);
                }}
                onFocus={() => {
                  setShowOperationDropdown(true);
                  setOperationSearch(selectedOperationLabel);
                }}
                placeholder={t('imputations.searchOperation')}
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary placeholder-slate-400"
              />
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">search</span>
              {showOperationDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  <div
                    onClick={() => {
                      setFilters((f) => ({ ...f, operation: '' }));
                      setOperationSearch('');
                      setShowOperationDropdown(false);
                    }}
                    className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100 font-medium text-slate-700"
                  >
                    {t('imputations.allOperations')}
                  </div>
                  {getFilteredOperations(operationSearch).map((op) => (
                    <div
                      key={op.id}
                      onClick={() => {
                        setFilters((f) => ({ ...f, operation: String(op.id) }));
                        setOperationSearch(`${op.production_line_lp || ''} – ${op.activity || ''}`.trim());
                        setShowOperationDropdown(false);
                      }}
                      className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100 last:border-b-0"
                    >
                      <div className="font-medium text-slate-900">{op.production_line_lp} – {op.activity}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('common:status')}</p>
            <div className="relative">
              <select
                value={filters.etat_final}
                onChange={(e) => setFilters((f) => ({ ...f, etat_final: e.target.value }))}
                className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
              >
                {etatFilterOptions.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
            </div>
          </div>
        </div>
        {hasActiveFilters && (
          <div className="mt-4">
            <button
              type="button"
              onClick={handleResetFilters}
              className="h-10 px-4 text-sm font-medium text-primary hover:text-blue-700 transition-colors"
            >
              {t('common:clearAllFilters')}
            </button>
          </div>
        )}
      </div>

      {/* Error - same style as employee page */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 px-4 py-3 rounded-lg flex items-center gap-2">
          <span className="material-symbols-outlined text-[20px]">error</span>
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* Main Content - table card same as employee page */}
      <div className="grid grid-cols-1 gap-6 items-start">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900">{t('imputations.managementTitle')}</h3>
              <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-1 rounded dark:bg-slate-700 dark:text-slate-300">
                {t('imputations.count', { count })}
              </span>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-slate-500 flex items-center gap-2">
                  <span className="material-symbols-outlined animate-spin text-[24px]">progress_activity</span>
                  {t('common:loading')}
                </div>
              </div>
            ) : results.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-slate-500">{t('imputations.noMatchFilters')}</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm table-auto whitespace-nowrap">
                  <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                    <tr>
                      <th className="px-4 py-3 font-semibold min-w-[100px]">{t('imputations.dateOperation')}</th>
                      <th className="px-4 py-3 font-semibold min-w-[120px]">{t('imputations.employee')}</th>
                      <th className="px-4 py-3 font-semibold min-w-[100px]">{t('common:team')}</th>
                      <th className="px-4 py-3 font-semibold min-w-[100px]">{t('imputations.center')}</th>
                      <th className="px-4 py-3 font-semibold min-w-[90px]">{t('imputations.operationId')}</th>
                      <th className="px-4 py-3 font-semibold min-w-[160px]">{t('imputations.operation')}</th>
                      {!isRsTenant && <th className="px-4 py-3 font-semibold min-w-[100px]">{t('imputations.projectType')}</th>}
                      <th className="px-4 py-3 font-semibold min-w-[100px]">{t('imputations.program')}</th>
                      <th className="px-4 py-3 font-semibold min-w-[100px]">{t('common:status')}</th>
                      <th className="px-4 py-3 font-semibold min-w-[90px]">{t('imputations.stdTime')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {results.map((row) => (
                      <React.Fragment key={row.id}>
                        <motion.tr
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="hover:bg-slate-50 transition-colors"
                        >
                          <td className="px-4 py-4 text-sm text-slate-500 whitespace-nowrap">{formatDate(row.date_operation)}</td>
                          <td className="px-4 py-4 text-sm text-slate-500">
                            {displayUser(row.employe)}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-500">{row.team_name || '—'}</td>
                          <td className="px-4 py-4 text-sm text-slate-500">{row.centre_name || '—'}</td>
                          <td className="px-4 py-4 text-sm text-slate-500">{row.gcr || '—'}</td>
                          <td className="px-4 py-4 text-sm text-slate-500">
                            {row.operation ? (
                              <>
                                {!isRsTenant && <div className="font-medium text-slate-900">{row.operation.production_line_lp}</div>}
                                <div className="text-xs text-slate-500">{row.operation.activity}</div>
                              </>
                            ) : '—'}
                          </td>
                          {!isRsTenant && (
                            <td className="px-4 py-4 text-sm text-slate-500">{row.project_type ?? row.operation?.production_type ?? '—'}</td>
                          )}
                          <td className="px-4 py-4 text-sm text-slate-500">{row.program ?? row.operation?.program ?? '—'}</td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${getEtatBadgeClasses(row.etat_final, getTenantSubdomain())}`}>
                              {row.etat_final_display || row.etat_final || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-500 whitespace-nowrap">{formatHoursToHHMM(row.temps_standard)}</td>
                        </motion.tr>
                        {showComments && row.commentaire && (
                          <tr className="bg-slate-50/70 hover:bg-slate-50">
                            <td colSpan={isRsTenant ? 9 : 10} className="px-4 py-2 text-sm text-slate-600 border-t-0 align-top">
                              <span className="font-medium text-slate-500 mr-2">{t('imputations.commentLabel')}</span>
                              {row.commentaire}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Pagination - same as employee page */}
            {!loading && count > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-500">
                    {t('common:showingRange', { from: startItem, to: endItem, total: count })}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">{t('common:perPage')}</span>
                    <div className="relative">
                      <select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        className="appearance-none rounded border border-slate-200 bg-white px-3 py-1.5 pr-8 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-[16px] pointer-events-none">arrow_drop_down</span>
                    </div>
                  </div>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={page === 1}
                      className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={t('common:firstPage')}
                    >
                      <span className="material-symbols-outlined text-sm">first_page</span>
                    </button>
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page <= 1}
                      className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={t('common:previousPage')}
                    >
                      <span className="material-symbols-outlined text-sm">chevron_left</span>
                    </button>
                    <div className="flex items-center gap-1">
                      {getPageNumbers().map((p, index) =>
                        p === '...' ? (
                          <span key={`ellipsis-${index}`} className="px-2 text-slate-400">...</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => handlePageChange(p)}
                            className={`h-8 w-8 flex items-center justify-center rounded border text-sm font-medium transition-colors ${
                              page === p ? 'bg-primary text-white border-primary' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                            }`}
                          >
                            {p}
                          </button>
                        )
                      )}
                    </div>
                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= totalPages}
                      className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={t('common:nextPage')}
                    >
                      <span className="material-symbols-outlined text-sm">chevron_right</span>
                    </button>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={page >= totalPages}
                      className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={t('common:lastPage')}
                    >
                      <span className="material-symbols-outlined text-sm">last_page</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImputationsTablePage;
